import { LOCATION_KINDS, LOCATION_PARENT_KINDS, type LocationKind } from '@loot/shared';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { Field, Input, issueFor, Select, Textarea } from '../../components/ui.tsx';

/**
 * Local: tipos, conversões e campos do formulário. Ficam fora da tela Locais (que carrega sob
 * demanda) porque a ficha da Empresa também os usa.
 */

export interface Unit {
  id: string;
  code: string;
  name: string;
  legalEntityCode: string;
}
export interface Place {
  id: string;
  branchId: string;
  parentId: string | null;
  kind: LocationKind;
  code: string;
  name: string;
  capacity: number | null;
  notes: string | null;
  isActive: boolean;
  isStorage: boolean;
  path: string;
}
export type LocationForm = {
  id?: string;
  branchId: string;
  parentId: string;
  kind: LocationKind;
  code: string;
  name: string;
  capacity: string;
  notes: string;
  isActive: boolean;
  isStorage: boolean;
};

export function locationUnitName(units: Unit[], id: string): string {
  const u = units.find((b) => b.id === id);
  return u ? `${u.legalEntityCode} · ${u.code} · ${u.name}` : '';
}

export function emptyLocationForm(branchId: string): LocationForm {
  return {
    branchId,
    parentId: '',
    kind: 'space',
    code: '',
    name: '',
    capacity: '',
    notes: '',
    isActive: true,
    isStorage: false,
  };
}

export function locationToForm(l: Place): LocationForm {
  return {
    id: l.id,
    branchId: l.branchId,
    parentId: l.parentId ?? '',
    kind: l.kind,
    code: l.code,
    name: l.name,
    capacity: l.capacity ? String(l.capacity) : '',
    notes: l.notes ?? '',
    isActive: l.isActive,
    isStorage: l.isStorage,
  };
}

/** Grava o local (o mesmo corpo na tela Locais e na ficha da Empresa). */
export function saveLocation(f: LocationForm) {
  const body = {
    branchId: f.branchId,
    parentId: f.parentId,
    kind: f.kind,
    code: f.code,
    name: f.name,
    capacity: f.capacity ? Number(f.capacity) : null,
    notes: f.notes,
    isActive: f.isActive,
    isStorage: f.kind === 'space' && f.isStorage,
  };
  return f.id ? api.put(`/core/locations/${f.id}`, body) : api.post('/core/locations', body);
}

/**
 * Campos do local, usados na tela Locais e na ficha da Empresa (aba Locais). Superiores possíveis:
 * mesma filial, ativos e de um nível acima.
 */
export function LocationFields({
  form,
  setForm,
  units,
  locations,
  error,
}: {
  form: LocationForm;
  setForm: (f: LocationForm) => void;
  units: Unit[];
  locations: Place[];
  error: unknown;
}) {
  const { t } = useTranslation();
  const parents = locations.filter(
    (l) =>
      l.branchId === form.branchId &&
      l.isActive &&
      l.id !== form.id &&
      (LOCATION_PARENT_KINDS[form.kind] as readonly string[]).includes(l.kind),
  );
  return (
    <>
      <Field label={t('facilities.fields.branch')} required>
        <Select
          disabled={!!form.id}
          value={form.branchId}
          onChange={(e) => setForm({ ...form, branchId: e.target.value, parentId: '' })}
        >
          {units.map((b) => (
            <option key={b.id} value={b.id}>
              {locationUnitName(units, b.id)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('facilities.locations.kind')} required>
        <Select
          disabled={!!form.id}
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value as LocationKind, parentId: '' })}
        >
          {LOCATION_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`facilities.locationKinds.${k}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label={t('facilities.locations.parent')}
        hint={t('facilities.locations.parentHint')}
        error={issueFor(error, 'parentId')}
      >
        <Select
          value={form.parentId}
          onChange={(e) => setForm({ ...form, parentId: e.target.value })}
        >
          <option value="">{t('facilities.locations.noParent')}</option>
          {parents.map((l) => (
            <option key={l.id} value={l.id}>
              {l.path}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label={t('facilities.locations.code')}
        hint={t('facilities.locations.codeHint')}
        error={issueFor(error, 'code')}
        required
      >
        <Input
          maxLength={30}
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
        />
      </Field>
      <Field label={t('facilities.locations.name')} error={issueFor(error, 'name')} required>
        <Input
          maxLength={200}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Field>
      <Field label={t('facilities.locations.capacity')} error={issueFor(error, 'capacity')}>
        <Input
          type="number"
          min={1}
          value={form.capacity}
          onChange={(e) => setForm({ ...form, capacity: e.target.value })}
        />
      </Field>
      <div className="sm:col-span-2">
        <Field label={t('facilities.locations.notes')}>
          <Textarea
            maxLength={2000}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
        />
        {t('fields.active')}
      </label>
      {form.kind === 'space' && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isStorage}
            onChange={(e) => setForm({ ...form, isStorage: e.target.checked })}
          />
          {t('facilities.locations.isStorage')}
        </label>
      )}
    </>
  );
}
